import {
  USER_ASSOCIATED_ANY,
  USER_ASSOCIATION_FILTER_KEY,
  USER_ID_FILTER_KEY,
} from 'Core-utils/helpers/findingsUserAssociationFilter';

export const USER_NAME_COLUMN_FILTER_TYPE = 'userNameColumn';

/** AG-Grid column id and URL param for the user name filter. */
export const USER_NAME_COLUMN_ID = 'user_name';

export const USER_NAME_FILTER_URL_KEYS = [
  USER_ASSOCIATION_FILTER_KEY,
  USER_NAME_COLUMN_ID,
  USER_ID_FILTER_KEY,
] as const;

export type UserNameColumnFilterMode = 'anyUser' | 'specific';

export type UserNameColumnFilterModel = {
  filterType: typeof USER_NAME_COLUMN_FILTER_TYPE;
  mode: UserNameColumnFilterMode;
  nameSearch?: string;
};

export function isUserNameColumnFilterModel(value: unknown): value is UserNameColumnFilterModel {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as UserNameColumnFilterModel).filterType === USER_NAME_COLUMN_FILTER_TYPE
  );
}

/** URL params owned by the user_name column filter. Undefined values should be omitted from the URL. */
export function mapUserNameFilterModelToUrlParams(
  model: UserNameColumnFilterModel | null | undefined
): Record<(typeof USER_NAME_FILTER_URL_KEYS)[number], string | undefined> {
  const cleared = {
    [USER_ASSOCIATION_FILTER_KEY]: undefined,
    [USER_NAME_COLUMN_ID]: undefined,
    [USER_ID_FILTER_KEY]: undefined,
  };

  if (!model) {
    return cleared;
  }

  if (model.mode === 'anyUser') {
    return {
      [USER_ASSOCIATION_FILTER_KEY]: USER_ASSOCIATED_ANY,
      [USER_NAME_COLUMN_ID]: undefined,
      [USER_ID_FILTER_KEY]: undefined,
    };
  }

  const nameSearch = model.nameSearch?.trim();
  if (model.mode === 'specific' && nameSearch) {
    return {
      [USER_ASSOCIATION_FILTER_KEY]: undefined,
      [USER_NAME_COLUMN_ID]: nameSearch,
      [USER_ID_FILTER_KEY]: undefined,
    };
  }

  return cleared;
}

export function mapUrlParamsToUserNameFilterModel(
  urlFilter: Record<string, unknown>
): UserNameColumnFilterModel | null {
  const userAssociated = urlFilter[USER_ASSOCIATION_FILTER_KEY];
  const rawName = urlFilter[USER_NAME_COLUMN_ID];
  const nameSearch =
    typeof rawName === 'string'
      ? rawName.trim()
      : Array.isArray(rawName) && typeof rawName[0] === 'string'
      ? rawName[0].trim()
      : '';

  if (nameSearch) {
    return {
      filterType: USER_NAME_COLUMN_FILTER_TYPE,
      mode: 'specific',
      nameSearch,
    };
  }

  if (userAssociated === USER_ASSOCIATED_ANY || userAssociated === 'true' || userAssociated === true) {
    return {
      filterType: USER_NAME_COLUMN_FILTER_TYPE,
      mode: 'anyUser',
    };
  }

  return null;
}

export function readUserNameColumnSearchTerms(
  filterModel: Record<string, unknown> | null | undefined
): string[] {
  const model = filterModel?.[USER_NAME_COLUMN_ID];
  if (isUserNameColumnFilterModel(model) && model.mode === 'specific') {
    const trimmed = model.nameSearch?.trim();
    return trimmed ? [trimmed] : [];
  }

  const legacyText = (model as { filter?: unknown } | undefined)?.filter;
  if (typeof legacyText === 'string' && legacyText.trim()) {
    return [legacyText.trim()];
  }

  return [];
}

export function readUserNameColumnHasUserAssociation(
  filterModel: Record<string, unknown> | null | undefined
): boolean {
  const model = filterModel?.[USER_NAME_COLUMN_ID];
  return isUserNameColumnFilterModel(model) && model.mode === 'anyUser';
}

export function applyUserNameFilterUrlParams(
  params: Record<string, unknown>,
  model: UserNameColumnFilterModel | null | undefined
): void {
  const userNameUrlParams = mapUserNameFilterModelToUrlParams(model);
  USER_NAME_FILTER_URL_KEYS.forEach((key) => {
    if (userNameUrlParams[key] !== undefined) {
      params[key] = userNameUrlParams[key];
    } else {
      delete params[key];
    }
  });
}
